import ThisBound from 'lib/object/this-bound';

export type Callable<T extends any[], R extends any = any> = (...args: T) => R;

export type EventMap = {
    [name: string]: any[];
}

export type Store<Types extends EventMap = EventMap> = {
    [K in keyof Types]?: Callable<Types[K]>[];
}

/**
 * Simple event bus, with optional but complete type safety.
 */
export default class Bus<Types extends EventMap = EventMap> extends ThisBound {
    private listeners: Store<Types> = {};

    constructor() {
        super();
    }

    getTypes(): Types {
        throw new Error('This function exists to extract the type information from the generic Types argument from the outside.');
    }

    on<N extends keyof Types>(event: N | N[], listener: Callable<Types[N]>): void {
        if (Array.isArray(event)) {
            event.forEach(name => this.on(name, listener));
        } else {
            // Append with new copy of array so that it doesn't change the size of the array
            // while the emit method iterates through the listeners, and so we don't copy
            // an array hundreds of times per second with repeated bus event emissions.
            this.listeners[event] = (this.listeners[event] ?? []).concat(listener);
        }
    }

    off<N extends keyof Types>(event: N | N[], listener: Callable<Types[N]>): void {
        if (Array.isArray(event)) {
            event.forEach(name => this.off(name, listener));
        } else {
            const stack = this.listeners[event];

            if (stack !== undefined) {
                const index = stack.lastIndexOf(listener);

                if (index !== -1) {
                    // Append with new copy of array so that it doesn't change the size of the array
                    // while the emit method iterates through the listeners, and so we don't copy
                    // an array hundreds of times per second with repeated bus event emissions.
                    this.listeners[event] = stack.slice(0, index).concat(stack.slice(index + 1));
                }
            }
        }
    }

    once<N extends keyof Types>(event: N | N[], listener: Callable<Types[N]>): void {
        const wrapper = this.makeWrapper(listener, () => this.off(event, wrapper));

        this.on(event, wrapper);
    }

    emit<N extends keyof Types, A extends Types[N]>(event: N, ...args: A): void {
        const listeners = this.listeners[event];

        if (listeners) {
            // Make a copy so that the length doesn't change during emission if
            // emission causes another event to be added.
            for (let i = 0, l = listeners.length; i < l; i++) {
                try {
                    listeners[i].apply(null, args);
                } catch (error) {
                    console.log(`Uncaught error while calling listener for bus event '${event}'`, listeners[i]);
                    console.error(error);
                }
            }
        }
    }

    makeWrapper<L extends Callable<any>>(listener: L, hook: Callable<any>): L {
        return function (...args: any) {
            listener(...args);

            hook();
        } as L;
    }

    removeAll<N extends keyof Types>(): void {
        for (const event in this.listeners) {
            if (this.listeners.hasOwnProperty(event)) {
                delete this.listeners[event];
            }
        }
    }
}
